/* Strumenti condivisi della sezione Ticket: chiamate al bot, costanti e selettori di ruoli/canali. */
import { useState } from "react";
import { toast } from "sonner";
import { X, Hash, Keyboard, Info, Plus, Loader2, FolderPlus } from "lucide-react";
import { apiError, botDelete, botGet, botPatch, botPost, botPut, http } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { inputC } from "@/components/kit";

export const ticketsApi = {
  systems: () => botGet("tickets/systems"),
  system: (id) => botGet(`tickets/systems/${id}`),
  create: (data) => botPost("tickets/systems", data),
  update: (id, data) => botPut(`tickets/systems/${id}`, data),
  setStatus: (id, status) => botPatch(`tickets/systems/${id}/status`, { status }),
  duplicate: (id, name) => botPost(`tickets/systems/${id}/duplicate`, { name }),
  remove: (id, deleteChannels) => botDelete(`tickets/systems/${id}?deleteChannels=${deleteChannels ? "true" : "false"}`),
  publish: (id) => botPost(`tickets/systems/${id}/publish`, {}),
  createCategory: (id, name) => botPost(`tickets/systems/${id}/category`, { name }),
  createLogs: (id, plan) => botPost(`tickets/systems/${id}/log-channels`, plan),
  stats: (systemId, days = 30) => botGet("tickets/stats", { ...(systemId ? { systemId } : {}), days }),
  items: (params) => botGet("tickets/items", params),
  item: (id) => botGet(`tickets/items/${id}`),
  close: (id, reason) => botPost(`tickets/items/${id}/close`, { reason }),
  reopen: (id) => botPost(`tickets/items/${id}/reopen`, {}),
  deleteTicket: (id) => botPost(`tickets/items/${id}/delete`, {}),
  transcript: (id) => botPost(`tickets/items/${id}/transcript`, {}),
  transcripts: (systemId) => botGet("tickets/transcripts", systemId ? { systemId } : undefined),
  deleteTranscript: (id) => botDelete(`tickets/transcripts/${id}`),
  blacklist: () => botGet("tickets/blacklist"),
  addBlacklist: (entry) => botPost("tickets/blacklist", entry),
  removeBlacklist: (id) => botDelete(`tickets/blacklist/${id}`),
};

/** Scarica l'HTML di una transcript (con l'autenticazione del sito) e lo apre o lo salva. */
export async function openTranscript(id, { download = false, name } = {}) {
  const { data } = await http.get(`/tickets/transcripts/${id}/html`, { responseType: "text" });
  const url = URL.createObjectURL(new Blob([data], { type: "text/html" }));
  if (download) {
    const a = document.createElement("a");
    a.href = url; a.download = name || `transcript-${id}.html`; a.click();
  } else {
    window.open(url, "_blank", "noopener");
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export const STATUS = {
  active: { label: "Attivo", tone: "#23A55A" },
  maintenance: { label: "Manutenzione", tone: "#FEE75C" },
  disabled: { label: "Disattivato", tone: "#80848E" },
};
export const TICKET_STATUS = {
  open: { label: "In attesa", tone: "#FEE75C" },
  claimed: { label: "In carico", tone: "#5865F2" },
  closed: { label: "Chiuso", tone: "#80848E" },
  deleted: { label: "Eliminato", tone: "#ED4245" },
};
export const LOG_KINDS = [
  { key: "open", label: "Apertura", desc: "Nuovi ticket e risposte al modulo", prefix: "open" },
  { key: "claim", label: "Presa in carico", desc: "Claim, rilascio e trasferimenti", prefix: "claim" },
  { key: "close", label: "Chiusura", desc: "Chiusure e riaperture", prefix: "close" },
  { key: "delete", label: "Eliminazione", desc: "Ticket eliminati, con transcript", prefix: "delete" },
  { key: "general", label: "Generale", desc: "Utenti aggiunti/rimossi, rinomine e tutto il resto", prefix: "log" },
];
export const EVENT_LABELS = {
  open: "Apertura", claim: "Presa in carico", unclaim: "Rilascio", close: "Chiusura", reopen: "Riapertura",
  delete: "Eliminazione", transfer: "Trasferimento", add: "Utente aggiunto", remove: "Utente rimosso", rename: "Rinomina",
};
export const ROLE_GROUPS = [
  { key: "staff", label: "Staff", desc: "Vede e gestisce tutti i ticket", info: "I ruoli che vedono i ticket di questo sistema, possono rispondere e usare i pulsanti. Vengono taggati quando si apre un ticket (se attivo)." },
  { key: "supervisor", label: "Supervisori", desc: "Come lo staff, e vedono sempre tutto", info: "Come lo staff, ma vedono sempre tutti i ticket (anche quelli presi in carico in modo privato) e possono fare ogni azione." },
  { key: "admin", label: "Amministratori", desc: "Accesso completo", info: "Accesso totale a questo sistema ticket. Chi è amministratore del server Discord ha comunque sempre accesso, anche se non è in questo elenco." },
  { key: "claim", label: "Possono prendere in carico", desc: "Vuoto = tutto lo staff", info: "Chi può premere «Prendi in carico» e diventare il responsabile del ticket. Se lasci vuoto, può farlo tutto lo staff." },
  { key: "close", label: "Possono chiudere / riaprire", desc: "Vuoto = tutto lo staff", info: "Chi può chiudere e riaprire i ticket. Se lasci vuoto, può farlo tutto lo staff." },
  { key: "delete", label: "Possono eliminare", desc: "Vuoto = tutto lo staff", info: "Chi può eliminare definitivamente il canale del ticket. Se lasci vuoto, può farlo tutto lo staff." },
  { key: "transcript", label: "Possono creare transcript", desc: "Vuoto = tutto lo staff", info: "Chi può creare a mano la transcript (la copia della conversazione) con il pulsante. Se lasci vuoto, può farlo tutto lo staff." },
  { key: "transfer", label: "Possono trasferire", desc: "Vuoto = tutto lo staff", info: "Chi può passare un ticket a un altro membro dello staff. Se lasci vuoto, può farlo tutto lo staff." },
];
export const LOG_INFO = {
  open: "Qui arriva un messaggio ogni volta che qualcuno apre un ticket, con le risposte al modulo.",
  claim: "Qui vedi chi prende in carico un ticket, chi lo lascia e i trasferimenti tra staff.",
  close: "Qui arrivano le chiusure (chi ha chiuso e il motivo) e le riaperture.",
  delete: "Qui arrivano i ticket eliminati, con il file della transcript allegato se attivo.",
  general: "Tutto il resto: utenti aggiunti o rimossi e rinomine. Se un altro tipo di log non ha un suo canale, finisce qui.",
};
export const EVENT_INFO = {
  open: "Qualcuno apre un nuovo ticket.", claim: "Uno staff prende in carico un ticket.", unclaim: "Lo staff lascia il ticket, che torna libero per gli altri.",
  close: "Un ticket viene chiuso.", reopen: "Un ticket chiuso viene riaperto.", delete: "Un ticket viene eliminato.",
  transfer: "Un ticket passa da uno staff a un altro.", add: "Una persona viene aggiunta al ticket.", remove: "Una persona viene tolta dal ticket.",
  rename: "Il canale del ticket cambia nome.",
};
export const BUTTON_INFO = {
  claim: "Lo staff si assegna il ticket e ne diventa il responsabile.", close: "Chiude il ticket (chiede il motivo, se previsto).",
  addUser: "Aggiunge un'altra persona al ticket: potrà vederlo e scrivere.", removeUser: "Toglie una persona che era stata aggiunta.",
  rename: "Cambia il nome del canale del ticket.", transfer: "Passa il ticket a un altro membro dello staff.",
  transcript: "Crea subito la copia di tutta la conversazione in un file.", reopen: "Riapre un ticket chiuso.", delete: "Elimina il canale del ticket (dopo una conferma).",
};
export const VAR_INFO = {
  "{username}": "nome utente di chi apre", "{display_name}": "nome visualizzato nel server", "{user_id}": "ID Discord di chi apre",
  "{ticket_id}": "codice unico del ticket", "{counter}": "numero progressivo (0001, 0002…)", "{category}": "nome del sistema", "{type}": "nome della tipologia",
};
export const BUTTON_LABELS = {
  claim: "🙋 Prendi in carico", close: "🔒 Chiudi", addUser: "➕ Aggiungi utente", removeUser: "➖ Rimuovi utente",
  rename: "✏️ Rinomina", transfer: "🔁 Trasferisci", transcript: "📜 Transcript", reopen: "🔓 Riapri", delete: "🗑️ Elimina",
};
export const NAME_VARS = ["{username}", "{display_name}", "{user_id}", "{ticket_id}", "{counter}", "{category}", "{type}"];
export const DELETE_AFTER = [
  { value: 0, label: "Mai" }, { value: 5, label: "5 minuti" }, { value: 10, label: "10 minuti" }, { value: 60, label: "1 ora" },
  { value: 360, label: "6 ore" }, { value: 720, label: "12 ore" }, { value: 1440, label: "24 ore" }, { value: 4320, label: "3 giorni" }, { value: 10080, label: "7 giorni" },
];
export const MAX_OPEN = [{ value: 1, label: "1" }, { value: 2, label: "2" }, { value: 3, label: "3" }, { value: 5, label: "5" }, { value: 0, label: "Illimitati" }];
export const COOLDOWN = [
  { value: 0, label: "Nessuno" }, { value: 30, label: "30 secondi" }, { value: 60, label: "1 minuto" }, { value: 300, label: "5 minuti" },
  { value: 600, label: "10 minuti" }, { value: 1800, label: "30 minuti" }, { value: 3600, label: "1 ora" },
];
export const DAYS = ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];
export const STYLE_COLORS = { primary: "#5865F2", secondary: "#4E5058", success: "#248046", danger: "#DA373C" };

export const slugify = (s) => (s || "").toLowerCase().normalize("NFKD").replace(/[^\w\s-]/g, "").trim().replace(/[\s_]+/g, "-").replace(/-+/g, "-").slice(0, 60);
export const logChannelName = (prefix, slug) => `${prefix}-${slug || "ticket"}${/ticket$/.test(slug || "ticket") ? "" : "-ticket"}`;
export const newLocalId = (p) => `${p}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

/** Esempio di nome del canale con i valori di un utente finto. */
export function previewName(template, system, typeLabel = "Supporto") {
  const counter = "1".padStart(system?.naming?.counterPad || 4, "0");
  const vars = { username: "mario", display_name: "Mario Rossi", user_id: "123456789012345678", ticket_id: "tk8f3a2c", counter, category: system?.name || "Supporto", type: typeLabel };
  const raw = (template || "").replace(/\{(\w+)\}/g, (m, k) => vars[k] ?? m);
  return raw.toLowerCase().replace(/\s+/g, "-").replace(/[^\p{L}\p{N}_-]/gu, "").replace(/-{2,}/g, "-").replace(/^-|-$/g, "") || "ticket";
}

// ─────────────────────────── icona «i» con la spiegazione di una voce ───────────────────────────
/** Si apre passando sopra col mouse o toccandola (telefono). */
export function InfoTip({ text, className = "" }) {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" aria-label="Cosa fa questa voce?" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(!open); }}
          onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
          className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full transition-colors hover:text-[#8ea0ff] ${open ? "text-[#8ea0ff]" : "text-[#6b7185]"} ${className}`}>
          <Info className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" className="w-72 border-[#2e3450] bg-[#1b1f2e] p-3 text-xs font-normal normal-case leading-relaxed tracking-normal text-[#DBDEE1] shadow-xl"
        onOpenAutoFocus={(e) => e.preventDefault()}>
        {text}
      </PopoverContent>
    </Popover>
  );
}

/** Etichetta con la «i» accanto. */
export const InfoLabel = ({ children, info }) => (
  <span className="inline-flex items-center gap-1.5">{children}<InfoTip text={info} /></span>
);

// ─────────────────────────── selettore ruoli: nome, colore e posizione ───────────────────────────
export function RolePicker({ value = [], onChange, placeholder = "+ Aggiungi ruolo", testId }) {
  const { roles } = useData();
  const list = roles.filter((r) => !r.isEveryone && !r.managed).sort((a, b) => (b.position ?? 0) - (a.position ?? 0));
  return (
    <div className="space-y-2" data-testid={testId}>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((id) => {
            const r = roles.find((x) => x.id === id);
            return (
              <span key={id} title={r ? `Posizione ${r.position} · ID ${r.id}` : `Ruolo eliminato (${id})`}
                className={`inline-flex items-center gap-1.5 rounded-full border py-1 pl-2 pr-1 text-xs ${r ? "border-[#232838] bg-[#131620] text-white" : "border-[#F23F43]/40 bg-[#F23F43]/10 text-[#ff9a9c]"}`}>
                <span className="h-2 w-2 rounded-full" style={{ background: r?.color || "#F23F43" }} />
                {r ? r.name : "⚠ ruolo eliminato"}
                {r && <span className="text-[10px] text-[#6b7185]">#{r.position}</span>}
                <button type="button" onClick={() => onChange(value.filter((v) => v !== id))} className="rounded-full p-0.5 text-[#949BA4] hover:bg-white/10 hover:text-white"><X className="h-3 w-3" /></button>
              </span>
            );
          })}
        </div>
      )}
      <Select value="" onValueChange={(id) => id && !value.includes(id) && onChange([...value, id])}>
        <SelectTrigger className={`${inputC} h-9`}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent className="max-h-72 border-[#232838] bg-[#161923] text-[#DBDEE1]">
          {list.filter((r) => !value.includes(r.id)).map((r) => (
            <SelectItem key={r.id} value={r.id} className="focus:bg-[#252B3D]">
              <span className="flex w-full items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: r.color || "#99AAB5" }} />
                <span>{r.name}</span>
                <span className="ml-2 text-[10px] text-[#6b7185]">pos. {r.position}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// ─────────────────────────── selettore canale con ID e inserimento manuale ───────────────────────────
const ICON = { text: "#", announcement: "📢", forum: "🗂️", voice: "🔊", stage: "🎙️", category: "📁" };

export function ChannelPick({ value, onChange, types = ["text", "announcement"], placeholder = "Seleziona un canale", allowNone = true, testId, suggestName = "", suggestParentId = null }) {
  const { channels } = useData();
  const [manual, setManual] = useState(false);
  const [creating, setCreating] = useState(false);
  const onlyCategory = types.length === 1 && types[0] === "category";
  const list = channels.filter((c) => types.includes(c.type));
  const current = value && channels.find((c) => c.id === value);
  const missing = value && !current;
  const parentName = (c) => channels.find((x) => x.id === c.parentId)?.name;
  return (
    <div className="space-y-1.5" data-testid={testId}>
      {manual ? (
        <Input value={value || ""} onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 20) || null)} placeholder="ID del canale (es. 1426202179064041585)" className={`${inputC} font-mono`} />
      ) : (
        <Select value={value || "__none__"} onValueChange={(v) => (v === "__create__" ? setCreating(true) : onChange(v === "__none__" ? null : v))}>
          <SelectTrigger className={`${inputC} ${missing ? "border-[#F23F43]/60" : ""}`}>
            <SelectValue placeholder={placeholder}>
              {missing ? <span className="text-[#ff9a9c]">⚠ Canale non trovato ({value})</span> : current ? <span><span className="mr-1 text-[#949BA4]">{ICON[current.type] || "#"}</span>{current.name}</span> : <span className="text-[#6b7185]">{placeholder}</span>}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="max-h-80 border-[#232838] bg-[#161923] text-[#DBDEE1]">
            <SelectItem value="__create__" className="border-b border-[#232838] focus:bg-[#5865F2]/20">
              <span className="font-semibold text-[#8ea0ff]">{onlyCategory ? "➕ Crea una nuova categoria…" : "➕ Crea un nuovo canale…"}</span>
            </SelectItem>
            {allowNone && <SelectItem value="__none__" className="focus:bg-[#252B3D]"><span className="text-[#949BA4]">— Nessuno —</span></SelectItem>}
            {list.length === 0 && <div className="px-3 py-2 text-xs text-[#949BA4]">Nessun canale disponibile</div>}
            {list.map((c) => (
              <SelectItem key={c.id} value={c.id} className="focus:bg-[#252B3D]">
                <span className="flex w-full items-center gap-1.5">
                  <span className="text-[#949BA4]">{ICON[c.type] || "#"}</span>{c.name}
                  {parentName(c) && <span className="text-[10px] text-[#6b7185]">in {parentName(c)}</span>}
                  <span className="ml-2 font-mono text-[10px] text-[#4b4d54]">{c.id}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {creating ? (
        <CreateChannelBox isCategory={onlyCategory} suggestName={suggestName} suggestParentId={suggestParentId}
          onCancel={() => setCreating(false)} onCreated={(id) => { setCreating(false); setManual(false); onChange(id); }} />
      ) : (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <button type="button" onClick={() => setCreating(true)} className="flex items-center gap-1 text-[11px] font-semibold text-[#8ea0ff] hover:text-white" data-testid={testId ? `${testId}-create` : undefined}>
            <Plus className="h-3 w-3" />{onlyCategory ? "Crea una nuova categoria" : "Crea un nuovo canale"}
          </button>
          <button type="button" onClick={() => setManual(!manual)} className="flex items-center gap-1 text-[11px] font-semibold text-[#949BA4] hover:text-white">
            {manual ? <><Hash className="h-3 w-3" />Scegli dall'elenco</> : <><Keyboard className="h-3 w-3" />Inserisci un ID a mano</>}
          </button>
        </div>
      )}
    </div>
  );
}

const slugChannel = (s) => (s || "").toLowerCase().trim().replace(/\s+/g, "-").replace(/[^\p{L}\p{N}_-]/gu, "").replace(/-{2,}/g, "-").slice(0, 100);

/** Riquadro per creare subito su Discord un canale (anche dentro una categoria nuova) o una categoria. */
function CreateChannelBox({ isCategory, suggestName, suggestParentId, onCancel, onCreated }) {
  const { channels, refresh } = useData();
  const categories = channels.filter((c) => c.type === "category");
  const [name, setName] = useState(isCategory ? (suggestName || "").toUpperCase() : slugChannel(suggestName));
  const [parent, setParent] = useState(suggestParentId && categories.some((c) => c.id === suggestParentId) ? suggestParentId : "__none__");
  const [newCat, setNewCat] = useState("");
  const [busy, setBusy] = useState(false);
  const create = async () => {
    const clean = isCategory ? name.trim() : slugChannel(name);
    if (!clean) return toast.error(isCategory ? "Scrivi il nome della categoria" : "Scrivi il nome del canale");
    if (!isCategory && parent === "__new__" && !newCat.trim()) return toast.error("Scrivi il nome della nuova categoria");
    setBusy(true);
    try {
      let parentId = parent === "__none__" ? null : parent;
      if (!isCategory && parent === "__new__") parentId = (await botPost("channels", { name: newCat.trim(), type: "category" })).id;
      const ch = await botPost("channels", isCategory ? { name: clean, type: "category" } : { name: clean, type: "text", parentId });
      await refresh();
      toast.success(isCategory ? `Categoria «${ch.name}» creata su Discord` : `Canale #${ch.name} creato su Discord`);
      onCreated(ch.id);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2.5 rounded-xl border border-[#5865F2]/40 bg-[#5865F2]/[0.07] p-3">
      <p className="flex items-center gap-1.5 text-xs font-bold text-white">
        {isCategory ? <FolderPlus className="h-3.5 w-3.5 text-[#8ea0ff]" /> : <Plus className="h-3.5 w-3.5 text-[#8ea0ff]" />}
        {isCategory ? "Nuova categoria su Discord" : "Nuovo canale testuale su Discord"}
      </p>
      <div className="flex items-center gap-1">
        {!isCategory && <span className="text-[#949BA4]">#</span>}
        <Input autoFocus value={name} onChange={(e) => setName(isCategory ? e.target.value.slice(0, 100) : e.target.value.toLowerCase().replace(/\s+/g, "-").slice(0, 100))}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); create(); } }}
          placeholder={isCategory ? "Es. TICKET SHOP" : "es. apri-ticket"} className={`${inputC} h-9 ${isCategory ? "" : "font-mono"}`} />
      </div>
      {!isCategory && (
        <>
          <Select value={parent} onValueChange={setParent}>
            <SelectTrigger className={`${inputC} h-9`}><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72 border-[#232838] bg-[#161923] text-[#DBDEE1]">
              <SelectItem value="__none__" className="focus:bg-[#252B3D]"><span className="text-[#949BA4]">Fuori dalle categorie</span></SelectItem>
              <SelectItem value="__new__" className="focus:bg-[#252B3D]"><span className="font-semibold text-[#8ea0ff]">➕ In una nuova categoria…</span></SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.id} className="focus:bg-[#252B3D]">📁 {c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {parent === "__new__" && <Input value={newCat} onChange={(e) => setNewCat(e.target.value.slice(0, 100))} placeholder="Nome della nuova categoria" className={`${inputC} h-9`} />}
        </>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} disabled={busy} className="rounded-lg px-3 py-1.5 text-xs font-semibold text-[#B5BAC1] hover:bg-white/5">Annulla</button>
        <button type="button" onClick={create} disabled={busy} className="flex items-center gap-1.5 rounded-lg bg-[#5865F2] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#4752c4] disabled:opacity-60">
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}Crea su Discord
        </button>
      </div>
    </div>
  );
}

export const StatusBadge = ({ status, map = STATUS }) => {
  const s = map[status] ?? { label: status, tone: "#80848E" };
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ background: `${s.tone}22`, color: s.tone }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.tone }} />{s.label}
    </span>
  );
};
